import React, { useState, useCallback, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { validateAndParseJSON, formatJSON, findJSONPath, JSONValidationResult, HighlightRange } from '../utils/jsonHighlightUtils';
import { usePlatform } from '../hooks/usePlatform';
import { readFromClipboard } from '../utils/clipboardUtils';
import { importFile, validateAndParseImportedJSON, exportToFile } from '../utils/fileUtils';
import { getInteractionClasses } from '../utils/platformUtils';

interface JsonInputProps {
  onJsonChange: (json: any) => void;
  onError: (error: string | null) => void;
  selectedPath?: string | null;
  enableJSON5?: boolean;
}

export interface JsonInputRef {
  reset: () => void;
  formatJSON: () => void;
  importFromFile: () => void;
  pasteFromClipboard: () => void;
  exportToFile: () => void;
}

const JsonInput = forwardRef<JsonInputRef, JsonInputProps>(({ 
  onJsonChange, 
  onError, 
  selectedPath = null,
  enableJSON5 = false 
}, ref) => {
  const { t } = useTranslation();
  const platform = usePlatform();
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<JSONValidationResult | null>(null);
  const [highlightRange, setHighlightRange] = useState<HighlightRange | null>(null);
  const [showHighlighter, setShowHighlighter] = useState(true);
  const [isMinified, setIsMinified] = useState(false);
  const [originalJson, setOriginalJson] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setJsonText('');
      setError(null);
      setValidation(null);
      setHighlightRange(null);
      setShowHighlighter(true);
      setIsMinified(false);
      setOriginalJson('');
      setImportStatus(null);
      onError(null);
      onJsonChange(null);
    },
    formatJSON: () => {
      if (jsonText.trim()) {
        try {
          const formatted = formatJSON(jsonText, enableJSON5);
          setJsonText(formatted);
          setIsMinified(false);
          validateAndUpdate(formatted);
        } catch (error) {
          // Format failed, keep original text
        }
      }
    },
    importFromFile: async () => {
      await handleFileImport();
    },
    pasteFromClipboard: async () => {
      await handlePasteFromClipboard();
    },
    exportToFile: () => {
      handleExportToFile();
    }
  }));

  const validateAndUpdate = useCallback((value: string) => {
    if (value.trim() === '') {
      setError(null);
      setValidation(null);
      setHighlightRange(null);
      setShowHighlighter(true);
      onError(null);
      onJsonChange(null);
      return;
    }

    const validationResult = validateAndParseJSON(value, enableJSON5);
    setValidation(validationResult);

    if (validationResult.isValid) {
      setError(null);
      setShowHighlighter(true);
      onError(null);
      onJsonChange(validationResult.parsed);
    } else {
      setError(validationResult.error || 'Invalid JSON');
      setShowHighlighter(false);
      onError(validationResult.error || 'Invalid JSON');
      onJsonChange(null);
    }
  }, [onJsonChange, onError, enableJSON5]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setJsonText(value);
    // Store original when user starts typing
    if (originalJson === '' && value.trim() !== '') {
      setOriginalJson(value);
    }
    validateAndUpdate(value);
  }, [validateAndUpdate, originalJson]);

  const toggleHighlighter = () => {
    setShowHighlighter(!showHighlighter);
  };

  const toggleMinifyPretty = () => {
    if (!jsonText.trim() || error) return;
    
    try {
      if (isMinified) {
        // Pretty print
        const formatted = formatJSON(jsonText, enableJSON5);
        setJsonText(formatted);
        setIsMinified(false);
        validateAndUpdate(formatted);
      } else {
        // Minify
        const validationResult = validateAndParseJSON(jsonText, enableJSON5);
        if (validationResult.isValid) {
          const minified = JSON.stringify(validationResult.parsed);
          setJsonText(minified);
          setIsMinified(true);
          validateAndUpdate(minified);
        }
      }
    } catch (error) {
      // Failed to format/minify
    }
  };

  // File import handler
  const handleFileImport = useCallback(async () => {
    setIsImporting(true);
    setImportStatus(null);
    
    try {
      const result = await importFile(platform);
      
      if (result.success && result.content) {
        const validation = validateAndParseImportedJSON(result.content);
        
        if (validation.isValid) {
          setJsonText(result.content);
          validateAndUpdate(result.content);
          setImportStatus(`Successfully imported ${result.fileName}`);
        } else {
          setImportStatus(`Invalid JSON in ${result.fileName}: ${validation.error}`);
        }
      } else {
        setImportStatus(result.error || 'Failed to import file');
      }
    } catch (error) {
      setImportStatus('Failed to import file');
    } finally {
      setIsImporting(false);
      // Clear status after 3 seconds
      setTimeout(() => setImportStatus(null), 3000);
    }
  }, [platform, validateAndUpdate]);

  const handleTextareaClick = useCallback(async (event: React.MouseEvent<HTMLTextAreaElement>) => {
    // Only trigger import if the textarea is completely empty
    if (!jsonText.trim()) {
      event.preventDefault();
      event.stopPropagation();
      await handleFileImport();
    }
  }, [jsonText, handleFileImport]);

  // Clipboard paste handler
  const handlePasteFromClipboard = async () => {
    try {
      const result = await readFromClipboard(platform);
      
      if (result.success && result.data) {
        const validation = validateAndParseImportedJSON(result.data);
        
        if (validation.isValid) {
          setJsonText(result.data);
          validateAndUpdate(result.data);
          setImportStatus('Successfully pasted from clipboard');
        } else {
          setImportStatus(`Invalid JSON from clipboard: ${validation.error}`);
        }
      } else {
        setImportStatus(result.error || 'Failed to read from clipboard');
      }
    } catch (error) {
      setImportStatus('Failed to read from clipboard');
    } finally {
      // Clear status after 3 seconds
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Export handler
  const handleExportToFile = () => {
    if (!jsonText.trim()) {
      setImportStatus('No content to export');
      setTimeout(() => setImportStatus(null), 3000);
      return;
    }

    try {
      const fileName = `json-export-${new Date().toISOString().split('T')[0]}.json`;
      exportToFile(jsonText, fileName, platform);
      setImportStatus('File exported successfully');
    } catch (error) {
      setImportStatus('Failed to export file');
    } finally {
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Update highlighting when selectedPath changes
  useEffect(() => {
    if (selectedPath && jsonText && validation?.isValid) {
      console.log('🔍 Searching for path:', selectedPath);
      const range = findJSONPath(jsonText, selectedPath);
      console.log('📍 Found range:', range);
      setHighlightRange(range);
      
      // Scroll to highlighted section
      if (range && textareaRef.current) {
        const lineHeight = 20; // Approximate line height
        const scrollTop = (range.line - 1) * lineHeight;
        textareaRef.current.scrollTop = Math.max(0, scrollTop - 100);
      }
    } else {
      setHighlightRange(null);
    }
  }, [selectedPath, jsonText, validation]);

  // Helper function to create background highlighting for textarea
  const createHighlightBackground = (text: string, range: HighlightRange): string => {
    const lineHeight = 20; // Match line height
    const topOffset = (range.line - 1) * lineHeight;
    
    // Calculate the number of lines to highlight
    const highlightText = text.substring(range.start, range.end);
    const highlightLines = highlightText.split('\n').length;
    const height = highlightLines * lineHeight;
    
    return `linear-gradient(to right, 
      rgba(254, 243, 199, 0.3) 0%, 
      rgba(254, 243, 199, 0.3) 100%) 
      0 ${topOffset}px / 100% ${height}px`;
  };

  const renderHighlightedTextarea = () => {
    if (!showHighlighter || !jsonText) {
      return (
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={jsonText}
            onChange={handleInputChange}
            onClick={handleTextareaClick}
            placeholder={!jsonText.trim() ? `${t('jsonInput.placeholder')} (Click to import file)` : t('jsonInput.placeholder')}
            className={`w-full h-full p-3 sm:p-4 border rounded-lg font-mono text-xs sm:text-sm resize-none focus:outline-none focus:ring-2 transition-all duration-200 bg-bg-white dark:bg-dark-bg-secondary text-text-dark dark:text-dark-text ${
              error 
                ? 'border-red-400 focus:ring-red-400 focus:border-red-400' 
                : 'border-border-light dark:border-dark-border focus:ring-primary-blue focus:border-primary-blue'
            } ${!jsonText.trim() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}`}
            style={{
              backgroundImage: highlightRange ? createHighlightBackground(jsonText, highlightRange) : 'none',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'local'
            }}
          />
        </div>
      );
    }

    return (
      <div className="flex-1 relative">
        <div className="absolute inset-0 overflow-auto border rounded-lg bg-bg-white dark:bg-dark-bg-secondary border-border-light dark:border-dark-border">
          <SyntaxHighlighter
            language="json"
            style={tomorrow}
            customStyle={{
              margin: 0,
              padding: window.innerWidth < 640 ? '12px' : '16px',
              background: 'transparent',
              fontSize: window.innerWidth < 640 ? '12px' : '14px',
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
            }}
            showLineNumbers={true}
            lineNumberStyle={{ 
              color: '#9CA3AF', 
              minWidth: '3em',
              paddingRight: '1em'
            }}
            lineProps={(lineNumber) => {
              const isHighlighted = highlightRange && 
                lineNumber >= highlightRange.line && 
                lineNumber <= (highlightRange.line + (jsonText.substring(highlightRange.start, highlightRange.end).split('\n').length - 1));
              
              return {
                style: {
                  backgroundColor: isHighlighted ? '#FEF3C7' : 'transparent',
                  display: 'block',
                  padding: '0 4px',
                  boxShadow: isHighlighted ? 'inset 3px 0 0 #F59E0B' : 'none',
                  borderLeft: isHighlighted ? '3px solid #F59E0B' : 'none',
                  fontWeight: isHighlighted ? '600' : 'normal'
                }
              };
            }}
          >
            {jsonText}
          </SyntaxHighlighter>
        </div>
        <textarea
          ref={textareaRef}
          value={jsonText}
          onChange={handleInputChange}
          onClick={handleTextareaClick}
          className="absolute inset-0 w-full h-full p-3 sm:p-4 font-mono text-xs sm:text-sm resize-none bg-transparent text-transparent caret-gray-900 dark:caret-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-primary-blue border border-transparent rounded-lg"
          style={{ 
            fontSize: window.innerWidth < 640 ? '12px' : '14px',
            lineHeight: '1.5',
            color: 'transparent',
            caretColor: 'currentColor'
          }}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-logo font-semibold text-primary-dark dark:text-dark-text mb-2">{t('jsonInput.title')}</h2>
            <p className="text-sm sm:text-base text-text-gray dark:text-dark-text-secondary font-primary">
              {enableJSON5 ? t('jsonInput.subtitleJson5') : t('jsonInput.subtitle')}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-2">
            {/* Import/Export Buttons - Always visible */}
            <button
              onClick={handleFileImport}
              disabled={isImporting}
              className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md ${getInteractionClasses(platform)} disabled:opacity-50 disabled:cursor-not-allowed bg-purple-600 dark:bg-purple-700 text-white hover:bg-purple-700 dark:hover:bg-purple-600`}
              title="Import JSON file"
              style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
            >
              <span className="hidden sm:inline">{isImporting ? 'Importing...' : 'Import'}</span>
              <span className="sm:hidden">📁</span>
            </button>
            
            <button
              onClick={handlePasteFromClipboard}
              className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md ${getInteractionClasses(platform)} bg-orange-600 dark:bg-orange-700 text-white hover:bg-orange-700 dark:hover:bg-orange-600`}
              title="Paste from clipboard"
              style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
            >
              <span className="hidden sm:inline">Paste</span>
              <span className="sm:hidden">📋</span>
            </button>

            {jsonText && (
              <>
                <button
                  onClick={handleExportToFile}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md ${getInteractionClasses(platform)} bg-teal-600 dark:bg-teal-700 text-white hover:bg-teal-700 dark:hover:bg-teal-600`}
                  title="Export to file"
                  style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
                >
                  <span className="hidden sm:inline">Export</span>
                  <span className="sm:hidden">💾</span>
                </button>

                <button
                  onClick={toggleHighlighter}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md ${getInteractionClasses(platform)} ${
                    showHighlighter 
                      ? 'bg-primary-blue dark:bg-dark-primary text-white hover:bg-blue-600 dark:hover:bg-blue-500'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  title={showHighlighter ? 'Syntax Highlighting deaktivieren' : 'Syntax Highlighting aktivieren'}
                  style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
                >
                  {showHighlighter ? '🎨' : '📝'}
                </button>
                
                <button
                  onClick={toggleMinifyPretty}
                  disabled={!!error}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md ${getInteractionClasses(platform)} disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 dark:bg-green-700 text-white hover:bg-green-700 dark:hover:bg-green-600`}
                  title={isMinified ? 'Pretty Print' : 'Minify'}
                  style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
                >
                  {isMinified ? '📖' : '🗜️'}
                </button>
                
                <button
                  onClick={() => {
                    if (textareaRef.current) {
                      try {
                        const formatted = formatJSON(jsonText, enableJSON5);
                        setJsonText(formatted);
                        setIsMinified(false);
                        validateAndUpdate(formatted);
                      } catch (error) {
                        // Format failed - show error briefly
                      }
                    }
                  }}
                  disabled={!!error}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md ${getInteractionClasses(platform)} disabled:opacity-50 disabled:cursor-not-allowed bg-primary-blue dark:bg-dark-primary text-white hover:bg-blue-600 dark:hover:bg-blue-500`}
                  title={t('jsonInput.formatTooltip')}
                  style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
                >
                  <span className="hidden sm:inline">{t('jsonInput.formatButton')}</span>
                  <span className="sm:hidden">✨</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col">
        {renderHighlightedTextarea()}
        
        {validation && !validation.isValid && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow-light">
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm text-red-700 dark:text-red-300 font-primary">
                <span className="font-medium">{t('errors.jsonError')}</span> {validation.error}
              </p>
              {validation.line && validation.column && (
                <span className="text-xs text-red-600 dark:text-red-400 font-mono">
                  {t('selectedPath.line')} {validation.line}:{validation.column}
                </span>
              )}
            </div>
            {validation.suggestion && (
              <p className="text-xs text-red-600 dark:text-red-400 font-primary mt-2">
                💡 <span className="font-medium">{t('errors.suggestion')}</span> {validation.suggestion}
              </p>
            )}
          </div>
        )}
        
        {importStatus && (
          <div className={`mt-2 p-2 rounded-lg border ${
            importStatus.includes('Successfully') || importStatus.includes('exported')
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
          }`}>
            <p className={`text-xs font-primary ${
              importStatus.includes('Successfully') || importStatus.includes('exported')
                ? 'text-green-800 dark:text-green-200'
                : 'text-orange-800 dark:text-orange-200'
            }`}>
              <span className="font-medium">
                {importStatus.includes('Successfully') || importStatus.includes('exported') ? '✅' : '⚠️'}
              </span> {importStatus}
            </p>
          </div>
        )}

        {selectedPath && highlightRange && (
          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-xs text-yellow-800 dark:text-yellow-200 font-primary">
              <span className="font-medium">{t('selectedPath.highlighted')}</span> {selectedPath} 
              <span className="ml-2 text-yellow-600 dark:text-yellow-400">
                ({t('selectedPath.line')} {highlightRange.line})
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

JsonInput.displayName = 'JsonInput';

export default JsonInput;